import { useMutation, useQuery } from "@apollo/client";
import { Menu, Transition } from "@headlessui/react";
import { ChevronUpDownIcon, PlusIcon, Cog8ToothIcon } from "@heroicons/react/24/solid";
import { ArrowRightStartOnRectangleIcon, ArrowsRightLeftIcon } from "@heroicons/react/24/outline";
import UserIcon from "components/UserIcon/UserIcon";
import { useSession } from "contexts";
import { GetMroEmployeeDocument, GetOrganizationsDocument, GetUserInfoDocument, SwitchOrganizationDocument } from "graphql/generated";
import { Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import isMobileApp from "utils/isMobileApp";
import { OrgTypes } from "utils/orgTypes";
import Button from "components/Button/Button";

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

export default function UserMenu({ sidebarOpen, altLoginUrl } 
    : { sidebarOpen?: boolean; altLoginUrl?: string}) {
    const { organizationId, setOrganizationId, logout, user, organizationType, setCraftId, setLoginSession, setOrganizationType } = useSession();
    const { data: { organizations } = { organizations: [] } } = useQuery(GetOrganizationsDocument, { variables: { where: { userOrganizationProfiles: { 
        some: { user: { is:{ id: { equals: user?.id } } } } 
    } } } });
    const [switchOrganization] = useMutation(SwitchOrganizationDocument);
    const { data: { mroEmployeeProfile } = {} } = useQuery(GetMroEmployeeDocument, {
        variables: { mroEmployeeId: user?.mroEmployeeProfileId },
        skip: !user?.mroEmployeeProfileId,
    }); 
    const { data: { me } = {}, client } = useQuery(GetUserInfoDocument);
    const navigate = useNavigate();
    const userNavigation = [
        { name: 'Settings', icon: Cog8ToothIcon, href: `${ organizationType === OrgTypes.PART145 ? '/mro' : ''}/account/settings` },
    ];
    const firstName =  organizationType === OrgTypes.PART145 ? mroEmployeeProfile?.firstName : (me?.firstName ?? "\xa0");
    const lastName =  organizationType === OrgTypes.PART145 ? mroEmployeeProfile?.lastName : (me?.lastName ?? "\xa0");
    const orgName = organizationType === OrgTypes.PART145 ? mroEmployeeProfile?.mroOrganization?.name : organizations.find((o) => o.id === organizationId)?.name;

    return (
      <>
        <Menu as="div" className={'md:group flex justify-end w-full' + (sidebarOpen ? '' : '')}>
          <Menu.Button className={'md:w-full' + (sidebarOpen ? ' p-2' : '')}>
            <div
              className={
                'flex rounded cursor-pointer transition-all pr-1 border border-brand bg-brand-electric/10 hover:shadow-inner hover:bg-brand ease-in-out' +
                (sidebarOpen ? '' : ' hidden')
              }>
              <div className={'flex items-center p-2 rounded-l transition w-full' + (sidebarOpen ? '' : ' hidden')}>
                <UserIcon
                  iconSize="lg"
                  iconText={firstName + ' ' + lastName}
                  iconId={firstName + ' ' + lastName}
                  iconColor={mroEmployeeProfile?.profileColor}
                  className=""
                />
                <div className="flex-col px-2 block overflow-hidden text-left text-ellipsis">
                  <span className="text-white font-bold text-left whitespace-nowrap">
                    {firstName} {lastName}
                  </span>
                  <span className="flex text-left text-xs text-brand-offwhite font-medium whitespace-nowrap -mt-1">
                    {orgName}
                  </span>
                </div>
              </div>
              <ChevronUpDownIcon className="h-auto w-8 px-1.5 text-white rounded-r hidden md:block" />
            </div>
            <div className="flex items-center w-full justify-end md:justify-center py-1">
              <UserIcon
                iconSize="md"
                iconText={firstName + ' ' + lastName}
                iconId={firstName + ' ' + lastName}
                iconColor={mroEmployeeProfile?.profileColor}
                className={sidebarOpen ? 'hidden' : 'hidden md:flex'}
              />
              <UserIcon
                iconSize="xl"
                iconText={firstName + ' ' + lastName}
                iconId={firstName + ' ' + lastName}
                iconColor={mroEmployeeProfile?.profileColor}
                className={'flex md:hidden'}
              />
            </div>
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95">
            <Menu.Items
              className={`absolute ${
                sidebarOpen ? 'md:w-[calc(100%-1.5rem)] left-3 md:-mb-4 origin-bottom bottom-24' : 'md:w-64 left-12 ml-1 bottom-2 origin-bottom-left'
              } focus:outline-none z-[99]`}
            >
              <div className="bg-brand text-white rounded border border-brand-electric py-2 w-full mr-2 shadow-lg shadow-brand">
                {organizationType === OrgTypes.PART91 && 
                  <div className="px-3 pb-2 border-b border-brand-electric mb-2">
                    <span className="text-xs font-medium uppercase">
                      {organizations?.length === 1 ? "Organization" : "Switch Organizations"}
                    </span>
                    <select 
                      disabled={organizations?.length === 1}
                      className="rounded border-0 bg-brand-dark h-12 md:h-10 mt-1 mb-2 px-2 text-white focus:shadow-inner placeholder:text-slate-400 placeholder:text-sm w-full pr-8 text-ellipsis"
                      onChange={async (e) => {
                        setOrganizationId(e.target.value);
                        setCraftId(null);
                        const result = await switchOrganization({ variables: { organizationId: e.target.value } });
                        if (result.data?.switchOrganization) {
                          await setLoginSession(result.data.switchOrganization.accessToken, result.data.switchOrganization.refreshToken);
                        }
                        await client.resetStore();
                        navigate('/crafts');
                      }}
                    >
                      {organizations?.map((item) => (
                        <option selected={organizationId === item.id} value={item.id} key={item.id}>{item.name}</option>
                      ))}
                    </select>
                    <Menu.Item key={'newOrg'}>
                      <Button 
                        icon={<PlusIcon className="h-4 w-4 mr-1"/>} 
                        text="New Organization" size="xs" className="w-full" 
                        color=""
                        onClick={() => {
                          navigate('/entities/new');
                        }}
                      />
                    </Menu.Item>
                  </div>
                }
                <div className="flex flex-col gap-1 text-sm px-2">
                  {userNavigation.map((item) => (
                    <Menu.Item key={item.name}>
                      {({ active }) => (
                        <Link
                          to={item.href}
                          className={classNames(
                            active ? 'bg-brand-dark rounded' : '',
                            'flex items-center gap-1.5 px-2 py-1 font-medium transition'
                          )}>
                          <item.icon className="h-4 w-4"/>
                          {item.name}
                        </Link>
                      )}
                    </Menu.Item>
                  ))}
                  {organizationType === OrgTypes.PART91 && !isMobileApp() && mroEmployeeProfile &&
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={classNames(
                            active ? 'bg-brand-dark rounded' : '',
                            'flex items-center gap-1.5 px-2 py-1 w-full text-left font-medium transition'
                          )}
                          onClick={async () => {
                            setOrganizationType(OrgTypes.PART145);
                            navigate('/mro/dashboard');
                          }}
                        >
                          <ArrowsRightLeftIcon className="h-4 w-4"/>
                          Switch to MRO
                        </button>
                      )}
                    </Menu.Item>
                  }
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={classNames(
                          active ? 'bg-brand-dark rounded' : '',
                          'flex items-center gap-1.5 px-2 py-1 w-full text-left font-medium transition'
                        )}
                        onClick={() => {
                          logout()
                            .then(() => {
                              navigate(altLoginUrl ?? '/auth');
                            })
                            .catch(console.error);
                        }}>
                        <ArrowRightStartOnRectangleIcon className="h-4 w-4"/>
                        Sign Out
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </>
    );
}

